import { useState, useEffect } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { RouterParams } from 'containers/AppRouter';
import ButtonSubmitData from 'shared/components/Button/ButtonSubmitData/ButtonSubmitData';
import Loader from 'shared/containers/Loader';
import { APIErrorT } from 'shared/models/api';
import { DeviceConfigT } from 'shared/models/device-configs';
import { FlowT } from 'shared/models/flows';
import * as Source from 'shared/models/sources';
import api from 'utils/api';

import DeviceConfigItemFormOne from './components/DeviceConfigItemFormOne';
import {
  APIFormBody,
  formOneValidator,
  FormValuesT,
} from './DeviceConfigManageData.validator';

function DeviceConfigEdit() {
  const methods = useForm<FormValuesT>({
    mode: 'onChange',
    resolver: yupResolver(formOneValidator),
  });
  const {
    handleSubmit,
    setValue,
    setError,
  } = methods;
  const history = useHistory();
  const { flowId, deviceProfileId, deviceConfigId } = useParams() as RouterParams;

  const [sourceTechnology, setSourceTechnology] = useState('');
  const [loadingState, setLoadingState] = useState<number>(0);
  const nextLoadingState = () => setLoadingState((count) => count + 1);
  const [poolId, setPoolId] = useState('');

  const loadSourceFromSourceId = (targetSourceId: string) => {
    const request = api.sources.getOne(targetSourceId);
    request
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
              history.push(`/flows/${flowId}`);
              nextLoadingState();
            });
        } else {
          resp.json()
            .then((source: Source.SourcesResultT) => {
              setSourceTechnology(source.technology);
              nextLoadingState();
            });
        }
      });
  };

  const loadDeviceConfigData = () => {
    api.deviceConfig.getOne(deviceConfigId as string, { flowId, deviceProfileId })
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
              history.push(`/flows/${flowId}`);
              nextLoadingState();
            });
        } else {
          resp.json()
            .then((d: DeviceConfigT) => {
              // Init values to edit
              setValue('name', d.name);
              setValue('comment', d.comment);
              setValue('deviceId', d.deviceId);
              nextLoadingState();
            });
        }
      });
  };

  const loadFlowData = () => {
    const request = api.flows.getOne(flowId as string);
    request
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              toast.error(error?.message);
            });
          nextLoadingState();
        } else {
          resp.json()
            .then((flow: FlowT) => {
              setPoolId(flow.poolId);
              loadSourceFromSourceId(flow.sourceId);
              nextLoadingState();
            });
        }
      });
  };

  useEffect(() => {
    loadDeviceConfigData();
    loadFlowData();
  }, []);

  const onSubmit: SubmitHandler<FormValuesT> = (formData: FormValuesT) => {
    const apiBodyRequest: APIFormBody = {
      name: formData.name,
      comment: formData.comment,
      deviceId: formData.deviceId,
      addressIPv6: formData.addressIPv6,
      addressIPv4: formData.addressIPv4,
      isIPv4: formData.addressIPv4 !== '',
      deviceProfileId: deviceProfileId as string,
      poolId,
    };
    const apiParams = { flowId, deviceProfileId };
    api.deviceConfig.update(deviceConfigId as string, apiBodyRequest, apiParams)
      .then((resp: any) => {
        if (resp.status >= 400) {
          resp.json()
            .then((error: APIErrorT) => {
              const errorMessage = error?.message;
              if (errorMessage === 'device ID already exists') {
                setError('deviceId', { message: 'ID already used - Please enter a new one' });
                return;
              }
              if (errorMessage === 'reservation pool is full') {
                toast.error('No more ip address to allocate for that device');
                return;
              }
              toast.error(errorMessage);
            });
        } else {
          resp.json()
            .then(() => {
              toast.success('Device has been edited');
              history.push(`/flows/${flowId}/device-profiles/${deviceProfileId}/devices/${deviceConfigId}`);
            });
        }
      });
  };

  if (loadingState < 2) {
    return <Loader.Page />;
  }
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="title__header">
          <h1>Device · Edit</h1>
          <ButtonSubmitData isEdit />
        </div>
        <div className="form scroll">
          <DeviceConfigItemFormOne
            deviceProfileId={deviceProfileId as string}
            isEdit
            sourceTechnology={sourceTechnology}
          />
        </div>
      </form>
    </FormProvider>
  );
}

export default DeviceConfigEdit;
